import { Component, Input, OnDestroy, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CommHubService } from 'src/app/providers/comm-hub/comm-hub.service';
import { UserService } from 'src/app/providers/user.service';
import { CommonService } from 'src/app/providers/common.service';
import { AuthService } from 'src/app/providers/auth.service';
import { apiLinks } from 'src/app/core/api-links/api-links';
import { getBroadcastAudienceUserTypes } from 'src/app/shared/constants/broadcast-audience-user-types';
import { getExtension } from 'src/app/shared/utils/file-type.util';
import { Subject, Observable, concat, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, switchMap, tap } from 'rxjs/operators';

export type AudienceType = 'everyone' | 'role' | 'email' | 'tpn';

interface RoleOption {
  name: string;
  displayName: string;
}

interface UserOption {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
}

interface TpnOption {
  id: string;
  tpn: string;
}

interface AttachmentMeta {
  url: string;
  fileName: string;
  fileType: string;
}

@Component({
  selector: 'app-broadcast-console',
  templateUrl: './broadcast-console.component.html',
  styleUrls: ['./broadcast-console.component.scss']
})
export class BroadcastConsoleComponent implements OnDestroy {
  @Input() variant: 'page' | 'modal' = 'page';

  readonly TITLE_MAX = 80;
  readonly BODY_MAX = 500;
  private readonly allowedAttachmentExtensions = new Set(['pdf', 'jpg', 'jpeg', 'png', 'gif', 'xls', 'xlsx']);

  form: FormGroup;
  sending = false;
  uploading = false;
  sentCount = 0;
  receiptsQueued = false;
  deliveryMode: string | null = null;
  broadcastSucceeded = false;
  attachment: AttachmentMeta | null = null;

  roleOptions: RoleOption[] = [];
  tpnOptions: TpnOption[] = [];

  userEmailsList: Observable<UserOption[]>;
  inputUserEmail$ = new Subject<string>();
  userSearching = false;

  tpnInput$ = new Subject<string>();
  tpnsLoading = false;

  audienceTypes: Array<{
    value: AudienceType;
    label: string;
    icon: string;
  }> = [
    { value: 'everyone', label: 'Everyone',  icon: 'icon-users' },
    { value: 'role',     label: 'User type', icon: 'icon-shield' },
    { value: 'email',    label: 'Email',     icon: 'icon-mail' },
    { value: 'tpn',      label: 'TPN',       icon: 'icon-shopping-bag' },
  ];

  selectAudienceType(value: string) {
    this.form.get('audienceType')?.setValue(value);
    this.onAudienceTypeChange();
  }
  
  audienceSummary(): string {
    if (this.audienceType === 'everyone') return 'Sending to everyone';
    const n = (this.form.get('targetValues')?.value || []).length;
    if (!n) return 'No recipients selected';
    if (this.audienceType === 'role') {
      return `Sending to ${n} user type${n > 1 ? 's' : ''}`;
    }
    if (this.audienceType === 'email') {
      return `Sending to ${n} email${n > 1 ? 's' : ''}`;
    }
    return `Sending to ${n} TPN${n > 1 ? 's' : ''}`;
  }
  tpns$ = concat(
    of([] as TpnOption[]),
    this.tpnInput$.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      tap(() => { this.tpnsLoading = true; }),
      switchMap((term) =>
        !term || term.trim().length < 2
          ? of([] as TpnOption[]).pipe(finalize(() => { this.tpnsLoading = false; }))
          : this.commonService.getResponseDataForFilter(apiLinks.tpnIds, {
            query: term.trim(),
            isFilter: true,
            page: 1,
            size: 25
          }).pipe(
            tap((items) => { this.tpnOptions = items || []; }),
            catchError(() => of([] as TpnOption[])),
            finalize(() => { this.tpnsLoading = false; })
          )
      )
    )
  );

  categories = [
    { value: 'general', label: 'General' },
    { value: 'alert', label: 'Alerts' },
    { value: 'announcement', label: 'Announcements' },
    { value: 'reminder', label: 'Reminder' }
  ];

  constructor(
    private commHub: CommHubService,
    private userService: UserService,
    private commonService: CommonService,
    private authService: AuthService,
    private fb: FormBuilder,
    @Optional() public activeModal: NgbActiveModal
  ) {
    this.form = this.fb.group({
      title: ['', [Validators.required, Validators.maxLength(this.TITLE_MAX)]],
      body: ['', [Validators.required, Validators.maxLength(this.BODY_MAX)]],
      category: ['general', Validators.required],
      audienceType: ['' as AudienceType, Validators.required],
      targetValues: [[] as string[]]
    });
    this.initUserEmailSearch();
  }

  initUserEmailSearch(): void {
    this.userEmailsList = concat(
      of([] as UserOption[]),
      this.inputUserEmail$.pipe(
        debounceTime(400),
        distinctUntilChanged(),
        tap(() => { this.userSearching = true; }),
        switchMap((term) => {
          if (term == null || term.trim().length < 4) {
            this.userSearching = false;
            return of([] as UserOption[]);
          }
          return this.commonService
            .getCommonHttpClientQueryParams(this.userService.getAllUserEmailsUrl, { search: term.trim() })
            .pipe(
              catchError(() => of([] as UserOption[])),
              tap(() => { this.userSearching = false; })
            );
        })
      )
    );
  }

  ngOnDestroy(): void {
    this.inputUserEmail$.complete();
    this.tpnInput$.complete();
  }

  get isModal(): boolean {
    return this.variant === 'modal' || !!this.activeModal;
  }

  get audienceType(): AudienceType {
    return this.form.value.audienceType;
  }

  get titleLength(): number {
    return (this.form.get('title')?.value || '').length;
  }

  get bodyLength(): number {
    return (this.form.get('body')?.value || '').length;
  }

  close(): void {
    this.activeModal?.close();
  }

  onAudienceTypeChange(): void {
    this.form.patchValue({ targetValues: [] });
    this.tpnOptions = [];

    if (this.audienceType === 'role') {
      this.loadRoles();
    }
  }

  loadRoles(): void {
    if (this.roleOptions.length) { return; }
    const actorUserType = this.authService.getUser?.userType || 'SYSTEM';
    this.roleOptions = getBroadcastAudienceUserTypes(actorUserType);
  }

  buildAudience(): { everyone?: boolean; roles?: string[]; emails?: string[]; tpns?: string[] } {
    const type = this.audienceType;
    const values: string[] = this.form.value.targetValues || [];

    if (type === 'everyone') {
      return { everyone: true };
    }
    if (type === 'role') {
      return { roles: values };
    }
    if (type === 'email') {
      return { emails: values };
    }
    if (type === 'tpn') {
      return { tpns: values };
    }
    return {};
  }

  hasAudienceSelection(): boolean {
    if (this.audienceType === 'everyone') { return true; }
    return (this.form.value.targetValues?.length || 0) > 0;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) { return; }

    const ext = getExtension(file.name);
    if (!this.allowedAttachmentExtensions.has(ext)) {
      input.value = '';
      return;
    }

    this.uploading = true;
    this.commHub.uploadFile(file).subscribe({
      next: (res) => {
        this.attachment = {
          url: res.url,
          fileName: res.fileName,
          fileType: res.fileType
        };
        this.uploading = false;
        input.value = '';
      },
      error: () => {
        this.uploading = false;
        input.value = '';
      }
    });
  }

  removeAttachment(): void {
    const url = this.attachment?.url;
    this.attachment = null;
    if (url) {
      this.commHub.deleteFile(url).subscribe({ error: () => {} });
    }
  }

  sendNow(): void {
    if (this.form.invalid || !this.hasAudienceSelection()) { return; }

    this.sending = true;
    this.sentCount = 0;
    this.receiptsQueued = false;
    this.deliveryMode = null;
    this.broadcastSucceeded = false;

    const idempotencyKey = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `bc-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    this.commHub.sendBroadcast({
      title: this.form.value.title,
      body: this.form.value.body,
      category: this.form.value.category,
      audience: this.buildAudience(),
      attachmentUrl: this.attachment?.url,
      attachmentName: this.attachment?.fileName,
      attachmentType: this.attachment?.fileType
    }, idempotencyKey).subscribe({
      next: (res) => {
        this.receiptsQueued = !!res?.receiptsQueued;
        this.sentCount = res?.sent || 0;
        this.deliveryMode = res?.deliveryMode || null;
        this.broadcastSucceeded = true;
        this.sending = false;
        this.form.reset({
          category: 'general',
          audienceType: 'everyone',
          targetValues: []
        });
        this.attachment = null;
      },
      error: () => {
        this.sending = false;
      }
    });
  }
}
